/* global Sentry __insp */
import { get } from 'lodash';

/* @ngInject */
export default class {
  constructor(
    $q,
    $state,
    $location,
    $log,
    $cookies,
    store,
    $timeout,
    jwtHelper,
    JwtClass,
    MsTeamsClass,
    UserClass,
    ProfileClass,
    $window,
    authManager,
    AuthClass
  ) {
    this.$q = $q;
    this.$window = $window;
    this.$state = $state;
    this.authManager = authManager;
    this.store = store;
    this.$cookies = $cookies;
    this.$timeout = $timeout;
    this.jwtHelper = jwtHelper;
    this.UserClass = UserClass;
    this.ProfileClass = ProfileClass;
    this.AuthClass = AuthClass;
    this.$location = $location;
    this.JwtClass = JwtClass;
    this.MsTeamsClass = MsTeamsClass;
    this.$log = $log;
  }

  redirect(redirect = true) {
    return this.$q((resolve, reject) => {
      return this.refreshLogin(redirect)
        .then(() => {
          const profileUid = this.UserClass.profileUid;

          if (!profileUid || !this.$state.$current || this.$state.$current.name === 'logout') return resolve();

          if (this.store.get('hash')) {
            this.$timeout(() => (window.location = this.store.get('hash')), 0);
            this.store.remove('hash');
          } else {
            this.$timeout(() => this.$state.go('app.users.dashboard', { id: profileUid }), 0);
          }

          return resolve('ResolveClass.redirected');
        })
        .catch(error => {
          this.$log.warn('ResolveClass.redirected', error);
          return reject(error);
        });
    });
  }

  login() {
    return this.$q((resolve, reject) => {
      this.ProfileClass.loadUser(this.JwtClass.getAuthToken)
        .then(profile => {
          if (profile.email) {
            __insp.push(['resetSession']);
            __insp.push(['identify', profile.email]);
            __insp.push([
              'tagSession',
              { email: profile.email, profile_uid: profile.profile_uid, company_id: profile.company_id }
            ]);

            if (PRODUCTION && typeof Sentry !== 'undefined') {
              Sentry.setUser({
                email: profile.email,
                profile_uid: profile.profile_uid,
                company_id: profile.company_id
              });
            }
          }
          if (!this.isMarketplaceRoute() && (!!profile.firstLogin || profile.signup))
            return resolve(this.$state.go('app.users.onboard', { id: profile.profile_uid }));
          if (this.store.get('hash')) {
            window.location = this.store.get('hash');
            this.store.remove('hash');
          } else {
            this.$timeout(() => this.$state.go('app.users.dashboard', { id: profile.profile_uid }), 0);
          }

          return resolve('ResolveClass.login');
        })
        .catch(error => {
          this.logout();
          this.$log.warn('ResolveClass.login');
          this.$log.error(error);
          reject(error);
          throw error;
        });
    });
  }

  logout() {
    this.authManager.unauthenticate();
    this.JwtClass.endSession();

    if (PRODUCTION && typeof Sentry !== 'undefined') {
      Sentry.configureScope(scope => scope.setUser(null));
    }

    return this.$q
      .all([this.UserClass.resetClass(), this.ProfileClass.resetClass()])
      .then(() => this.$q.reject('logout'))
      .catch(error => {
        if (error !== 'logout') this.$log.error(error);
        throw error;
      });
  }

  refreshLogin(redirect = true, route = undefined) {
    return this.$q((resolve, reject) => {
      try {
        if (PRODUCTION && typeof Sentry !== 'undefined') {
          // set sentry session
          if (this.ProfileClass.user_profile.email) {
            __insp.push(['identify', this.ProfileClass.user_profile.email]);
            __insp.push([
              'tagSession',
              {
                email: this.ProfileClass.user_profile.email,
                profile_uid: this.ProfileClass.user_profile.profile_uid,
                company_id: this.ProfileClass.user_profile.company_id
              }
            ]);

            Sentry.setUser({
              email: this.ProfileClass.user_profile.email,
              profile_uid: this.ProfileClass.user_profile.profile_uid,
              company_id: this.ProfileClass.user_profile.company_id
            });
          }
        }

        if (!redirect && !this.JwtClass.getAuthToken) {
          return resolve(this.AuthClass.redirectToAuthenticationUrl(redirect));
        }

        // Exit early user logged in
        if (this.UserClass.loggedIn) {
          return resolve('logged in');
        }

        const currentPath = window.location.hash.replace(/(#\/)|(\?(.*))/g, '');

        this.$log.debug({ currentPath });
        if (
          redirect &&
          currentPath.length &&
          currentPath !== 'logout' &&
          currentPath !== 'password' &&
          currentPath !== 'auth0' &&
          currentPath !== 'registration' &&
          currentPath !== 'register' &&
          currentPath !== 'auth' &&
          currentPath !== 'signup' &&
          !this.JwtClass.getRefreshToken
        ) {
          this.store.set('params', this.$state.current);
          this.store.set('hash', window.location.hash);
          return reject('No Token');
        }

        if (this.JwtClass.getAuthToken && !this.jwtHelper.isTokenExpired(this.JwtClass.getAuthToken)) {
          return this.ProfileClass.loadUser(this.JwtClass.getAuthToken, true)
            .then(profile => {
              if (route) {
                this.isReadOnly(route);
              }
              if (
                !this.isMarketplaceRoute() &&
                (profile.firstLogin || profile.firstLogin === undefined) &&
                profile.profile_uid
              ) {
                return resolve(this.$state.go('app.users.onboard', { id: profile.profile_uid }));
              }
              return resolve('logged in');
            })
            .catch(error => {
              if (error.message === 'readOnly') {
                return resolve(this.$state.go('app.readOnly', { product: route }));
              }

              this.logout();
              this.$log.warn('ResolveClass.refreshLogin 1 ');
              this.$log.error(error);
              return reject('No Token');
            });
        }

        if (redirect && !this.JwtClass.getRefreshToken) {
          if (this.MsTeamsClass.inTeams()) {
            // Running inside a Microsoft Teams tab?
            return this.MsTeamsClass.ssologin()
              .then(response => {
                if (response === 'invalid_grant') {
                  return resolve('request consent');
                }
                return this.AuthClass.authenticateMsTeamsSso(response).then(() => resolve('logged in'));
              })
              .catch(error => {
                this.$log.warn('ResolveClass.MsTeamsClass.ssologin Error ');
                this.$log.error(error);
                return reject('Failed to login to MSTeams SSO');
              });
          }

          return this.AuthClass.redirectToAuthenticationUrl(redirect)
            .then(() => resolve('Bottom Resolve'))
            .catch(error => {
              this.$log.warn('ResolveClass.status Error', error);
              resolve(this.logout()); // LOGOUT on any error response
            });
        }

        return this.JwtClass.status()
          .then(token => this.ProfileClass.loadUser(token, true))
          .then(() => resolve('logged in'))
          .catch(error => {
            this.$log.warn('ResolveClass.status Error', error);

            return this.logout(); // LOGOUT on any error response
          })
          .then(() => resolve('logged out'));
      } catch (error) {
        reject(error);
      }
    });
  }

  setAdminOnlyAccess(config, adminOnly = false) {
    if (config.adminOnly || adminOnly) {
      config.disabled = !this.UserClass.isAdmin || !this.UserClass.isTeamLeader;
    }

    return config;
  }

  applicationSettings(feature, adminOnly = false) {
    const defaultConfig = {
      adminOnly: false,
      active: false,
      disabled: false,
      collapsed: false
    };

    const config = get(
      this.ProfileClass,
      `profile_complete.company.preferences.application_settings.${feature}`,
      defaultConfig
    );

    return this.setAdminOnlyAccess(config, adminOnly);
  }

  isUpsell(path) {
    const upsell = get(this.ProfileClass, `profile_complete.company.preferences.application_upsell.${path}`, false);

    if (upsell) {
      throw new Error('upsell');
    }
  }

  isReadOnly(path) {
    const readOnly = get(this.ProfileClass, `profile_complete.company.preferences.read_only.${path}`, false);

    if (readOnly) {
      throw new Error('readOnly');
    }
  }

  isAllowedCompanyRoute(route) {
    // Pages that dont have preference toggle
    const companyAccessObj = {
      49: {
        '/compliance': () => {
          return !!this.UserClass.isAdmin;
        }
      }
    };

    // Pages with preference toggle
    const preferenceAccessObj = {
      '/admin/courses': () => {
        this.isUpsell('admin.courses');
        return true;
      },
      '/admin/courses/activation': () => {
        this.isReadOnly('admin.courseActivation');
        return true;
      },
      '/admin/courses/:id/review': () => {
        this.isReadOnly('admin.courseReview');
        return true;
      },
      '/admin/approvals/cpe-requests': () => {
        this.isReadOnly('admin.approvalsCPEReqs');
        return true;
      },
      '/admin/approvals/certificates': () => {
        this.isReadOnly('admin.approvalsCerts');
        return true;
      },
      '/admin/courses/:courseId': () => {
        this.isUpsell('admin.coursesID');
        return true;
      },
      '/admin/learning': () => {
        this.isUpsell('admin.learning');
        return true;
      },
      '/learning/activities': () => {
        this.isReadOnly('user.learningActivities');
        return true;
      },
      '/admin/learning/:id': () => {
        this.isReadOnly('admin.learningTrackEdit');
        return true;
      },
      '/learning/trackId': () => {
        this.isReadOnly('user.learningTrackId');
        return true;
      },
      '/admin/event-manager': () => {
        this.isUpsell('admin.eventManager');
        return true;
      },
      '/admin/employees/:id/compliance': () => {
        this.isReadOnly('admin.employeesCompliance');
        return true;
      },
      '/admin/employees/:id/:certId/edit': () => {
        this.isReadOnly('admin.employeesCertEdit');
        return true;
      },
      '/admin/event-manager/:courseid/events': () => {
        this.isUpsell('admin.eventManagerID');
        return true;
      },
      '/admin/sponsors-manager': () => {
        this.isUpsell('admin.sponsorManager');
        this.isReadOnly('admin.sponsorManager');
        return true;
      },
      '/admin/settings/company': () => {
        this.isUpsell('admin.settings.company');
        this.isReadOnly('admin.settings.company');
        return true;
      },
      '/admin/settings/users': () => {
        this.isUpsell('admin.settings.users');
        this.isReadOnly('admin.settings.users');
        return true;
      },
      '/admin/settings/emails': () => {
        this.isUpsell('admin.settings.emails');
        this.isReadOnly('admin.settings.emails');
        return true;
      },
      '/admin/settings/emails/:type_id': () => {
        this.isUpsell('admin.settings.emailsID');
        return true;
      },
      '/admin/settings/forms': () => {
        this.isUpsell('admin.settings.forms');
        return true;
      },
      '/admin/settings/integrations': () => {
        this.isUpsell('admin.settings.integrations');
        this.isReadOnly('admin.settings.integrations');
        return true;
      },
      '/admin/settings/tags': () => {
        this.isUpsell('admin.settings.tags');
        this.isReadOnly('admin.settings.tags');
        return true;
      },
      '/admin/settings/teams': () => {
        this.isUpsell('admin.settings.teams');
        this.isReadOnly('admin.settings.teams');
        return true;
      },
      '/admin/settings/practice-areas': () => {
        this.isReadOnly('admin.settings.practice-areas');
        return true;
      },
      '/admin/settings/departments': () => {
        this.isReadOnly('admin.settings.departments');
        return true;
      },
      '/admin/settings/locations': () => {
        this.isReadOnly('admin.settings.locations');
        return true;
      },
      '/admin/settings/levels': () => {
        this.isReadOnly('admin.settings.levels');
        return true;
      },
      '/author/:id/activities': () => {
        this.isUpsell('author.activities');
        this.isReadOnly('admin.activities');
        return true;
      },
      '/author/:id/activities/:activityId': () => {
        this.isReadOnly('admin.activityEdit');
        return true;
      },
      '/author/:id/courses': () => {
        this.isUpsell('author.courses');
        this.isReadOnly('admin.courseEdit');
        return true;
      },
      '/author/:id/courses/:courseId': () => {
        this.isUpsell('author.coursesID');
        return true;
      },
      '/author/:id/evaluation-forms': () => {
        this.isUpsell('author.evaluationForms');
        this.isReadOnly('user.evaluationForms');
        return true;
      },
      '/certificates/hours': () => {
        this.isReadOnly('user.addHours');
        const adminOnly = get(this.ProfileClass, 'profile_complete.company.preferences.certificates.adminOnly', false);
        return !adminOnly || !!this.UserClass.isAdmin || !!this.UserClass.isTeamLeader;
      },
      '/certificates/:certId/edit': () => {
        this.isReadOnly('user.editCert');
        const adminOnly = get(this.ProfileClass, 'profile_complete.company.preferences.certificates.adminOnly', false);
        return !adminOnly || !!this.UserClass.isAdmin || !!this.UserClass.isTeamLeader;
      },
      '/certifications': () => {
        const adminOnly = get(
          this.ProfileClass,
          'profile_complete.company.preferences.certifications.adminOnly',
          false
        );
        return !adminOnly || !!this.UserClass.isAdmin || !!this.UserClass.isTeamLeader;
      },
      '/licenses': () => {
        this.isReadOnly('admin.settingsLicenses');
        const adminOnly = get(this.ProfileClass, 'profile_complete.company.preferences.licenses.adminOnly', false);
        return !adminOnly || !!this.UserClass.isAdmin || !!this.UserClass.isTeamLeader;
      },
      '/users/certificates': () => {
        this.isReadOnly('user.certs');
        return true;
      },
      '/evaluation': () => {
        this.isReadOnly('user.evaluation');
        return true;
      },
      '/event-manager/:id/event-info': () => {
        this.isReadOnly('admin.eventInfo');
        return true;
      },
      '/event-manager/:id/add-ons': () => {
        this.isReadOnly('admin.eventIntegration');
        return true;
      },
      '/event-manager/:id/materials': () => {
        this.isReadOnly('admin.eventMaterials');
        return true;
      },
      '/event-manager/:id/evaluations': () => {
        this.isReadOnly('admin.eventEvaluations');
        return true;
      },
      '/event-manager/:id/emails': () => {
        this.isReadOnly('admin.eventEmails');
        return true;
      },
      '/courses/:courseId/evaluation': () => {
        this.isReadOnly('user.evaluation');
        return true;
      },
      '/certificates/:certId/edit/upload': () => {
        this.isReadOnly('user.uploadCert');
        return true;
      },
      '/compliance': () => {
        this.isReadOnly('user.compliance');
        return true;
      },
      '/:courseId': () => {
        this.isReadOnly('user.courseEdit');
        return true;
      },
      '/forms': () => {
        this.isReadOnly('admin.forms');
        return true;
      },
      '/users/:id/teams': () => {
        this.isReadOnly('user.teams');
        return true;
      },
      '/users/:id/settings/profiles': () => {
        this.isReadOnly('user.settingsProfile');
        return true;
      },
      '/users/:id/settings/certifications': () => {
        this.isReadOnly('user.settingsCertification');
        return true;
      },
      '/users/:id/settings/attachments': () => {
        this.isReadOnly('user.settingsAttachment');
        return true;
      },
      '/activities/:id': () => {
        this.isReadOnly('user.activities');
        return true;
      },
      '/activities': () => {
        this.isReadOnly('user.activities');
        return true;
      },
      '/courses/:id': () => {
        this.isReadOnly('user.courses');
        return true;
      },
      '/events/:id': () => {
        this.isReadOnly('user.events');
        return true;
      },
      '/catalog': () => {
        this.isReadOnly('user.catalog');
        return true;
      },
      '/internal': () => {
        this.isReadOnly('user.internal');
        return true;
      },
      '/courses': () => {
        this.isReadOnly('user.courses');
        return true;
      },
      '/events': () => {
        this.isReadOnly('user.events');
        return true;
      },
    };

    const companyAccess =
      !companyAccessObj[this.ProfileClass.company_id] ||
      !companyAccessObj[this.ProfileClass.company_id][route] ||
      companyAccessObj[this.ProfileClass.company_id][route]();

    const companyPreference = !preferenceAccessObj[route] || preferenceAccessObj[route]();

    return companyAccess && companyPreference;
  }

  isAdminRoute() {
    return !!(this.$state.$current && this.$state.$current.includes && this.$state.$current.includes['app.admin']);
  }

  isAuthorRoute() {
    return !!(this.$state.$current && this.$state.$current.includes && this.$state.$current.includes['app.author']);
  }

  isEventManagerRoute() {
    return !!(
      this.$state.$current &&
      this.$state.$current.includes &&
      this.$state.$current.includes['app.eventManager']
    );
  }

  isMarketplaceRoute() {
    return !!(
      this.$location.path().includes('bundles') ||
      this.$location.path().includes('courses') ||
      this.$location.path().includes('events')
    );
  }

  isManageRoute() {
    return this.isAdminRoute() || this.isAuthorRoute() || this.isEventManagerRoute();
  }

  authenticateAdmin() {
    return this.$q((resolve, reject) => {
      if (this.UserClass.loggedIn && (this.UserClass.isAdmin || this.UserClass.isTeamLeader)) return resolve();
      reject('Not Admin');
    });
  }

  authenticateAuthor() {
    return this.$q((resolve, reject) => {
      if (this.UserClass.loggedIn && this.UserClass.isAuthor) return resolve();
      reject('Not Author');
    });
  }

  authenticateEventCoordinator() {
    return this.$q((resolve, reject) => {
      if (this.UserClass.loggedIn && (this.UserClass.isEventCoordinator || this.UserClass.isAdmin)) return resolve();
      reject('Not Event Coordinator');
    });
  }

  authenticateCompany(route) {
    return this.$q((resolve, reject) => {
      try {
        const companyAccess = this.isAllowedCompanyRoute(route);
        if (companyAccess) {
          return resolve();
        }
        return resolve(this.$state.go('app.users.dashboard', { id: this.UserClass.profileUid }));
      } catch (e) {
        if (e.message === 'upsell') {
          return resolve(this.$state.go('app.upgrade', { product: route }));
        }
        if (e.message === 'readOnly') {
          return resolve(this.$state.go('app.readOnly', { product: route }));
        }
        reject(e);
      }
    });
  }

  previousStateInfo() {
    const $state = this.$state;

    const params = {
      Name: $state.current.name,
      Params: { ...$state.params },
      URL: $state.href($state.current.name, $state.params),

      Previous: function(defaultState) {
        if (this.URL) {
          return $state.go(this.Name, this.Params);
        }

        return $state.go(defaultState.Name, defaultState.Params);
      }
    };

    return params;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/ResolveClass.js